import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  FileText, ArrowLeft, Loader2, CheckCircle2, XCircle, AlertTriangle,
  Lock, Unlock, Send, RotateCcw, DollarSign, Package, Building, Calendar,
  User, Info, CreditCard, ScrollText
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import RecordAuditTrail from '../components/audit/RecordAuditTrail';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const IRView = () => {
  const navigate = useNavigate();
  const { invoice_document: invoiceDocument, fiscal_year: fiscalYear } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [viewTab, setViewTab] = useState('overview');


  const loadInvoice = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/invoices/${invoiceDocument}/${fiscalYear}`);
      setInvoice(response.data);
    } catch (error) {
      console.error('Error loading invoice:', error);
      toast.error('Failed to load invoice');
      navigate('/p2p/ir-list');
    } finally {
      setLoading(false);
    }
  }, [invoiceDocument, fiscalYear, navigate]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  const handlePost = async () => {
    if (!window.confirm('Are you sure you want to post this invoice to accounting?')) return;
    
    try {
      setActionLoading(true);
      await axios.post(`${BACKEND_URL}/api/invoices/${invoiceDocument}/${fiscalYear}/post`);
      toast.success('Invoice posted successfully!');
      loadInvoice();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBlock = async () => {
    const reason = window.prompt('Enter block reason code (R=Price, Q=Quantity, M=Manual):', 'M');
    if (!reason) return;
    
    try {
      setActionLoading(true);
      await axios.post(`${BACKEND_URL}/api/invoice-receipts/${invoiceDocument}/${fiscalYear}/block?block_reason=${reason}`);
      toast.success('Invoice blocked');
      loadInvoice();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnblock = async () => {
    if (!window.confirm('Are you sure you want to unblock this invoice?')) return;
    
    try {
      setActionLoading(true);
      await axios.post(`${BACKEND_URL}/api/invoice-receipts/${invoiceDocument}/${fiscalYear}/unblock`);
      toast.success('Invoice unblocked');
      loadInvoice();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unblock invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReverse = async () => {
    if (!window.confirm('Are you sure you want to reverse this invoice? This action cannot be undone.')) return;
    
    try {
      setActionLoading(true);
      await axios.post(`${BACKEND_URL}/api/invoice-receipts/${invoiceDocument}/${fiscalYear}/reverse`);
      toast.success('Invoice reversed');
      loadInvoice();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reverse invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'PARKED': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'POSTED': 'bg-green-100 text-green-800 border-green-200',
      'PAID': 'bg-orange-100 text-orange-800 border-orange-200',
      'BLOCKED': 'bg-red-100 text-red-800 border-red-200',
      'REVERSED': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getMatchStatusColor = (status) => {
    const colors = {
      'MATCHED': 'bg-green-100 text-green-800',
      'VARIANCE': 'bg-yellow-100 text-yellow-800',
      'BLOCKED': 'bg-red-100 text-red-800',
      'FAILED': 'bg-red-100 text-red-800',
      'PENDING': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getMatchStatusIcon = (status) => {
    switch (status) {
      case 'MATCHED': return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'VARIANCE': return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      case 'BLOCKED':
      case 'FAILED': return <XCircle className="w-5 h-5 text-red-600" />;
      default: return <Info className="w-5 h-5 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading invoice...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!invoice) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <p className="text-gray-600">Invoice not found</p>
          <Button onClick={() => navigate('/p2p/ir-list')} className="mt-4">
            Back to List
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/p2p/ir-list')}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-7 h-7 text-orange-600" />
                Invoice Receipt: {invoice.invoice_document}
              </h1>
              <p className="text-sm text-gray-500">
                Fiscal Year: {invoice.fiscal_year} | Vendor Invoice: {invoice.vendor_invoice_number}
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {invoice.status === 'PARKED' && !invoice.blocked && (
              <Button onClick={handlePost} disabled={actionLoading} className="bg-green-600 hover:bg-green-700">
                <Send className="w-4 h-4 mr-2" />Post
              </Button>
            )}
            {invoice.status === 'PARKED' && !invoice.blocked && (
              <Button variant="outline" onClick={handleBlock} disabled={actionLoading}>
                <Lock className="w-4 h-4 mr-2" />Block
              </Button>
            )}
            {invoice.blocked && (
              <Button variant="outline" onClick={handleUnblock} disabled={actionLoading}>
                <Unlock className="w-4 h-4 mr-2" />Unblock
              </Button>
            )}
            {invoice.status !== 'REVERSED' && invoice.status !== 'PAID' && (
              <Button variant="outline" onClick={handleReverse} disabled={actionLoading} className="text-red-600 hover:text-red-700">
                <RotateCcw className="w-4 h-4 mr-2" />Reverse
              </Button>
            )}
          </div>
        </div>

        {/* Status Badges */}
        <div className="flex items-center gap-3 mb-6">
          <span className={`px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(invoice.status)}`}>
            Status: {invoice.status}
          </span>
          {invoice.match_status && (
            <span className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 ${getMatchStatusColor(invoice.match_status)}`}>
              {getMatchStatusIcon(invoice.match_status)}
              Match: {invoice.match_status}
            </span>
          )}
          {invoice.blocked && (
            <span className="px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-800 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Blocked: {invoice.block_reason_text || invoice.block_reason}
            </span>
          )}
        </div>

        <div className="flex gap-1 border-b border-gray-200 mb-6">
          <button onClick={() => setViewTab('overview')} data-testid="ir-tab-overview"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${viewTab === 'overview' ? 'border-orange-600 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            Overview
          </button>
          <button onClick={() => setViewTab('audit')} data-testid="ir-tab-audit"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${viewTab === 'audit' ? 'border-orange-600 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <ScrollText className="w-4 h-4" /> Audit Trail
          </button>
        </div>

        {viewTab === 'overview' && (<>
        {/* Summary Card */}
        <div className="bg-gradient-to-r from-orange-50 to-orange-50 rounded-lg border border-orange-200 p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div>
              <p className="text-xs text-orange-600 flex items-center gap-1"><Building className="w-3 h-3" />Vendor</p>
              <p className="font-semibold text-orange-900">{invoice.vendor_id}</p>
              <p className="text-xs text-orange-700">{invoice.vendor_name}</p>
            </div>
            <div>
              <p className="text-xs text-orange-600 flex items-center gap-1"><CreditCard className="w-3 h-3" />Company Code</p>
              <p className="font-semibold text-orange-900">{invoice.company_code}</p>
            </div>
            <div>
              <p className="text-xs text-orange-600 flex items-center gap-1"><Calendar className="w-3 h-3" />Invoice Date</p>
              <p className="font-semibold text-orange-900">{invoice.invoice_date}</p>
            </div>
            <div>
              <p className="text-xs text-orange-600 flex items-center gap-1"><Calendar className="w-3 h-3" />Posting Date</p>
              <p className="font-semibold text-orange-900">{invoice.posting_date}</p>
            </div>
            <div>
              <p className="text-xs text-orange-600 flex items-center gap-1"><User className="w-3 h-3" />Created By</p>
              <p className="font-semibold text-orange-900">{invoice.created_by}</p>
            </div>
            <div>
              <p className="text-xs text-orange-600">Invoice Type</p>
              <p className="font-semibold text-orange-900">{invoice.invoice_type}</p>
            </div>
          </div>
        </div>

        {/* 3-Way Match Results */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-orange-600" />
            3-Way Match Results (PO - GR - Invoice)
          </h2>
          
          <div className={`p-4 rounded-lg border mb-4 ${getMatchStatusColor(invoice.match_status)}`}>
            <div className="flex items-center gap-3 mb-2">
              {getMatchStatusIcon(invoice.match_status)}
              <span className="font-semibold text-lg">{invoice.match_status}</span>
            </div>
            <p className="text-sm">{invoice.match_summary || 'Match status details'}</p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-xs text-gray-500 mb-1">Total Quantity Variance</p>
              <p className={`text-xl font-bold ${invoice.total_quantity_variance !== 0 ? 'text-yellow-700' : 'text-green-700'}`}>
                {(invoice.total_quantity_variance || 0).toFixed(2)}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-xs text-gray-500 mb-1">Total Price Variance</p>
              <p className={`text-xl font-bold ${Math.abs(invoice.total_price_variance || 0) > 0 ? 'text-yellow-700' : 'text-green-700'}`}>
                {invoice.currency} {(invoice.total_price_variance || 0).toFixed(2)}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-xs text-gray-500 mb-1">Total Amount Variance</p>
              <p className={`text-xl font-bold ${Math.abs(invoice.total_amount_variance || 0) > 100 ? 'text-red-700' : 'text-green-700'}`}>
                {invoice.currency} {(invoice.total_amount_variance || 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Invoice Totals */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Invoice Totals
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <p className="text-xs text-orange-600">Net Amount</p>
              <p className="text-xl font-bold text-orange-900">
                {invoice.currency} {(invoice.total_net_amount || invoice.total_net || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <p className="text-xs text-orange-600">Tax Amount</p>
              <p className="text-xl font-bold text-orange-900">
                {invoice.currency} {(invoice.total_tax_amount || invoice.total_tax || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-xs text-green-600">Gross Amount</p>
              <p className="text-xl font-bold text-green-900">
                {invoice.currency} {(invoice.total_gross_amount || invoice.total_gross || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-xs text-gray-600">Line Items</p>
              <p className="text-xl font-bold text-gray-900">{invoice.items?.length || 0}</p>
            </div>
          </div>
        </div>

        {/* Accounting & Payment Info */}
        {(invoice.accounting_document || invoice.payment_document) && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Accounting & Payment</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {invoice.accounting_document && (
                <div>
                  <p className="text-xs text-gray-500">Accounting Document</p>
                  <p className="font-semibold text-gray-900">{invoice.accounting_document}</p>
                </div>
              )}
              {invoice.posted_at && (
                <div>
                  <p className="text-xs text-gray-500">Posted At</p>
                  <p className="font-semibold text-gray-900">{new Date(invoice.posted_at).toLocaleString()}</p>
                </div>
              )}
              {invoice.payment_document && (
                <div>
                  <p className="text-xs text-gray-500">Payment Document</p>
                  <p className="font-semibold text-gray-900">{invoice.payment_document}</p>
                </div>
              )}
              {invoice.payment_date && (
                <div>
                  <p className="text-xs text-gray-500">Payment Date</p>
                  <p className="font-semibold text-gray-900">{invoice.payment_date}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Line Items */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-orange-600" />
            Invoice Line Items
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Ref</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Material</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Amount</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tax</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Match</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invoice.items?.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-orange-600">{item.item_number}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium">{item.po_number}</p>
                      <p className="text-xs text-gray-500">Item {item.po_item}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium">{item.material_id}</p>
                      <p className="text-xs text-gray-500 truncate max-w-[200px]">{item.material_description}</p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <p className="font-medium">{(item.quantity || 0).toFixed(2)}</p>
                      <p className="text-xs text-gray-500">{item.unit_of_measure}</p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <p className="font-medium">{(item.unit_price || 0).toFixed(2)}</p>
                      {item.price_variance !== 0 && (
                        <p className={`text-xs ${item.price_variance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {item.price_variance > 0 ? '+' : ''}{item.price_variance?.toFixed(2)} vs PO
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      {item.currency} {(item.net_amount || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {(item.tax_amount || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getMatchStatusColor(item.item_match_status || 'PENDING')}`}>
                        {item.item_match_status || 'OK'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Header Text */}
        {invoice.header_text && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Notes</h2>
            <p className="text-gray-700">{invoice.header_text}</p>
          </div>
        )}
        </>)}
        {viewTab === 'audit' && (
          <RecordAuditTrail doctype="invoice_receipt" recordId={invoiceDocument} />
        )}
      </div>
    </Layout>
  );
};

export default IRView;
